import { useInfiniteQuery } from '@tanstack/react-query';
import {
  DropdownEntry,
  DropdownMenu,
  DropdownRadioGroup,
  Portal,
} from '@toggle/design-system';
import {
  getTimeTillEndOfDayMs,
  useDisableScroll,
  useScrollListener,
} from '@toggle/helpers';
import { Citation } from '@toggle/toggle';
import React, { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CitationSortOrder,
  getCitations,
} from '~/api/citation/citation-service';
import { useApp } from '~/stores/use-app/useApp';
import { DocumentResponse } from '~/stores/use-chat/assistant-response.type';
import { Message } from '~/stores/use-chat/use-chat.types';

import { DocumentEmpty } from '../document-viewer/document-empty/DocumentEmpty';
import { HighlightTextConfig } from '../document-viewer/document-highlight-utils/document-highlight-utils';
import { DocumentLoader } from '../document-viewer/document-loader/DocumentLoader';
import { DocumentViewer } from '../document-viewer/DocumentViewer';
import { DocumentSource } from './document-source/DocumentSource';
import { DocumentSourcesHeader } from './document-sources-header/DocumentSourcesHeader';
import * as S from './DocumentSourcesView.styles';

const QUERY_KEY = 'DOCUMENT_SOURCES';

export const CITATION_GROUP_ID = 'CITATION_GROUP_ID';

interface DocumentSourcesViewProps {
  chatMessage: Message;
  messageIndex: number;
  onClose: () => void;
  citationId: string;
}

export const DocumentSourcesView: FC<DocumentSourcesViewProps> = ({
  chatMessage,
  messageIndex,
  citationId,
  onClose,
}) => {
  const { t } = useTranslation(['intel', 'common']);
  const [selectedPill, setSelectedPill] = useState<CitationSortOrder>(0);
  const [selectedCitation, setSelectedCitation] = useState<Citation>();

  const containerRef = useRef<HTMLUListElement>(null);

  const isSidebarExpanded = useApp(state => state.isSidebarExpanded);
  const staleTime = getTimeTillEndOfDayMs();

  const onClick = (citation: Citation) => {
    setSelectedCitation(citation);
  };

  const queryResult = useInfiniteQuery({
    queryKey: [QUERY_KEY, citationId, selectedPill],
    queryFn: async ({ pageParam }) =>
      getCitations(citationId, {
        page: pageParam,
        sort_by: selectedPill,
      }),
    initialPageParam: 0,
    staleTime,
    getNextPageParam: (lastData, _, pageParam) =>
      lastData.citations.length < 20 ? null : pageParam + 1,
  });

  useScrollListener(
    containerRef,
    () => {
      if (queryResult?.hasNextPage) {
        queryResult?.fetchNextPage();
      }

      return queryResult?.hasNextPage;
    },
    [queryResult],
    { isGlobalScrollListened: false, offset: 200 }
  );

  useDisableScroll();

  const pillOptions: DropdownEntry[] = [
    {
      key: 'relevant',
      label: t('intel:documentSources.relevant'),
      onClick: () => setSelectedPill(0),
    },
    {
      key: 'recent',
      label: t('intel:documentSources.recent'),
      onClick: () => setSelectedPill(1),
    },
  ];

  const citations = queryResult.data?.pages.flatMap(p => p.citations);

  const highlightTexts: HighlightTextConfig['highlightTexts'] = useMemo(() => {
    if (
      !citations ||
      !selectedCitation ||
      selectedCitation.document_kind === 'presentation'
    ) {
      return [];
    }

    return [
      {
        text: selectedCitation.text.trim().replace(/\s\s*/g, ' '),
        pages: selectedCitation.pages,
        isPrimary: true,
      },
    ];
  }, [citations, selectedCitation]);

  const docsOverview = (chatMessage.reply as DocumentResponse[])[0]?.data
    .overview;

  const resolvedDocumentCount = t('documentCount', {
    documentCount: docsOverview.documents,
    count: docsOverview.documents,
  });
  const resolvedCompanyCount = t('companyCount', {
    companyCount: docsOverview.companies,
    count: docsOverview.companies,
  });

  const resolvedPassages = t('passageCount', {
    passageCount: queryResult.data?.pages[0].total_count ?? 0,
    count: queryResult.data?.pages[0].total_count ?? 0,
  });

  return (
    <Portal>
      <S.Container $isSidebarExpanded={isSidebarExpanded}>
        <DocumentSourcesHeader
          messageIndex={messageIndex}
          question={chatMessage.question}
          onClose={onClose}
        />
        <S.BodyContainer>
          <S.SourcesSection>
            {queryResult.data && (
              <S.SourcesHeader>
                <S.SourcesListHeader>
                  {t('intel:sourcesListHeader', {
                    passageCount: resolvedPassages,
                    documentCount: resolvedDocumentCount,
                    companyCount: resolvedCompanyCount,
                  })}
                </S.SourcesListHeader>
                <DropdownMenu
                  triggerNode={
                    <S.StyledChip
                      type="choice"
                      label={pillOptions[selectedPill].label}
                      rightIcon="ChevronLightDown"
                      rightIconSize={12}
                    />
                  }
                  contentProps={{ asChild: true }}
                >
                  <S.DropdownWrapper>
                    <DropdownRadioGroup
                      items={pillOptions}
                      selectedKey={pillOptions[selectedPill].key}
                    />
                  </S.DropdownWrapper>
                </DropdownMenu>
              </S.SourcesHeader>
            )}
            {!!citations?.length ? (
              <S.List ref={containerRef}>
                {citations.map((citation, idx) => (
                  <DocumentSource
                    key={idx}
                    citation={citation}
                    onClick={onClick}
                    isSelected={selectedCitation === citation}
                  />
                ))}
              </S.List>
            ) : (
              <DocumentLoader />
            )}
          </S.SourcesSection>
          {selectedCitation ? (
            <DocumentViewer
              selectedCitation={selectedCitation}
              highlightTexts={highlightTexts}
            />
          ) : (
            <DocumentEmpty />
          )}
        </S.BodyContainer>
      </S.Container>
    </Portal>
  );
};
